"use client";

import { useLayoutEffect, useRef } from "react";
import { useModalState } from "@/context/ModalContext";
import {
  isEventInsideModalDialog,
  lockPageScroll,
  unlockPageScroll,
  type PageScrollLockSnapshot,
} from "@/helpers/lockPageScroll";

/**
 * Always-mounted lock for every ModalContext popup (RFI, brochure, login,
 * Call Us, 30s banner, video, share, etc.). Independent of the lazy modal host
 * so the page cannot scroll for a frame before the dialog chunk paints.
 */
export default function ModalPageScrollLock() {
  const { isModalOpen } = useModalState();
  const scrollYRef = useRef(0);
  const snapshotRef = useRef<PageScrollLockSnapshot | null>(null);

  useLayoutEffect(() => {
    if (!isModalOpen) return;

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    scrollYRef.current = scrollY;
    snapshotRef.current = lockPageScroll(scrollY);

    const preventBackgroundScroll = (event: Event) => {
      if (isEventInsideModalDialog(event.target)) return;
      event.preventDefault();
    };

    const preventBackgroundKeys = (event: KeyboardEvent) => {
      const keys = [
        " ",
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
      ];
      if (!keys.includes(event.key)) return;
      if (isEventInsideModalDialog(event.target)) return;
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      event.preventDefault();
    };

    window.addEventListener("wheel", preventBackgroundScroll, { passive: false });
    window.addEventListener("touchmove", preventBackgroundScroll, {
      passive: false,
    });
    window.addEventListener("keydown", preventBackgroundKeys);

    return () => {
      window.removeEventListener("wheel", preventBackgroundScroll);
      window.removeEventListener("touchmove", preventBackgroundScroll);
      window.removeEventListener("keydown", preventBackgroundKeys);
      if (snapshotRef.current) {
        unlockPageScroll(snapshotRef.current, scrollYRef.current);
        snapshotRef.current = null;
      }
    };
  }, [isModalOpen]);

  return null;
}
